import React, { useContext } from 'react'
import PrintIcon from '@material-ui/icons/Print'
import DownloadIcon from '@material-ui/icons/PictureAsPdf'
import DeclineIcon from '@material-ui/icons/Cancel'
import MoreIcon from '@material-ui/icons/ExpandMore'
import TranslateIcon from '@material-ui/icons/Translate'

import { Button, VIEW_IN_LABELS, PopupMenu, PopupMenuItem, useTranslation } from '@paintscout/ui'
import { useClientOptions } from '@paintscout/ui-business'
import type { QuoteDocument } from 'paintscout'
import { getObjectLabels } from '@paintscout/util/builder'

import { usePresentationNav } from '../PresentationNavContext'

import { QuoteContext } from '../../../context/QuoteContext'
import { getLanguageSwitcherTargets } from './language-switcher'

export interface PresentationNavMoreProps {}

const WORK_ORDER_VIEW = 'work-order'
const PRODUCT_ORDER_FORM_VIEW = 'product-order-form'

export default function PresentationNavMore({ ..._props }: PresentationNavMoreProps) {
  const { options } = useClientOptions()
  const { __, lang } = useTranslation()

  const navContext = usePresentationNav()
  const { onMoreClick, view, availableLangs } = navContext

  const { quote } = useContext(QuoteContext)

  const allowResponse = navContext.allowResponse && view !== WORK_ORDER_VIEW && !quote?.is_invoice

  const objectLabels = getObjectLabels({ options, quote })

  const allowDecline = allowResponse && options?.options?.allowDecline && view !== PRODUCT_ORDER_FORM_VIEW

  const declineLabel = getDeclineLabel({ quote, objectLabels })
  const printLabel = getActionLabel('Print')
  const downloadLabel = getActionLabel('Download')
  const languageTargets = getLanguageSwitcherTargets(lang, availableLangs)

  return (
    <PopupMenu
      component={
        <Button icon={MoreIcon} variant={'text'}>
          {__('More')}
        </Button>
      }
    >
      <PopupMenuItem
        icon={PrintIcon}
        onClick={(ev) => {
          onMoreClick(ev, 'print')
        }}
      >
        {printLabel}
      </PopupMenuItem>
      <PopupMenuItem
        icon={DownloadIcon}
        onClick={(ev) => {
          onMoreClick(ev, 'download')
        }}
      >
        {downloadLabel}
      </PopupMenuItem>
      {view === WORK_ORDER_VIEW &&
        languageTargets.map((l) => (
          <PopupMenuItem
            key={l}
            icon={TranslateIcon}
            onClick={() => navContext.onLanguageChange?.(l === 'en' ? undefined : l)}
          >
            {VIEW_IN_LABELS[l] ?? l}
          </PopupMenuItem>
        ))}
      {allowDecline && (
        <PopupMenuItem
          icon={DeclineIcon}
          onClick={(ev) => {
            onMoreClick(ev, 'decline')
          }}
        >
          {declineLabel}
        </PopupMenuItem>
      )}
    </PopupMenu>
  )

  function getActionLabel(action: 'Print' | 'Download') {
    if (view === WORK_ORDER_VIEW) {
      return `${__(action)} ${__(objectLabels.workOrder.value)}`
    } else if (view === PRODUCT_ORDER_FORM_VIEW) {
      return `${__(action)} ${__('Product Order Form')}`
    } else {
      return `${__(action)} ${__(objectLabels.quote.value)}`
    }
  }

  function getDeclineLabel({
    quote,
    objectLabels
  }: {
    quote: QuoteDocument
    objectLabels: ReturnType<typeof getObjectLabels>
  }) {
    if (objectLabels.declineButton) {
      return objectLabels.declineButton.value
    } else {
      const item =
        quote?.is_invoice && quote?.totals?.pending
          ? __(objectLabels.additionalWork.value)
          : __(objectLabels.quote.value)
      return `${__('Decline')} ${item}`
    }
  }
}
